import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Row, Col, Space } from 'antd';
import { FaSave, FaTimes } from 'react-icons/fa';
import {SaveFilled} from "@ant-design/icons";
import { parseISO, format, isValid } from 'date-fns';
import Table from '../Table';
import { useApi } from '../../hooks/api';
import { ITableColumn } from '../../interfaces/table';

interface ILicenseExtraPackageFormProps {
  licenseId: string;
  onSave: (packageValue: number) => Promise<void>;
  onCancel: () => void;
}

interface IExtraPackageCurrentMonth {
  package_value: number;
  hiring_date: string;
  credit: boolean;
}

const LicenseExtraPackageForm: React.FC<ILicenseExtraPackageFormProps> = ({ licenseId, onSave, onCancel }) => {
  const { api } = useApi();
  const [extraPackageValue, setExtraPackageValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [currentMonthPackages, setCurrentMonthPackages] = useState<IExtraPackageCurrentMonth[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  const formatCurrency = (value: any) => {
    if (value === undefined || value === null || value === '') return '';
    const num = Number(String(value).replace(/\./g, '').replace(',', '.'));
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(num)) return value;
    return num.toFixed(2).replace('.', ',');
  };

  const parseCurrency = (value: string) => {
    if (!value) return 0;
    return Number(value.replace(/\./g, '').replace(',', '.'));
  };

  const formatPackageValue = (value: number) => {
    if (value === undefined || value === null) return '';
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };

  const formatHiringDate = (value: string) => {
    if (!value) return '';
    const date = parseISO(value);
    if (!isValid(date)) return value;
    return format(date, 'dd/MM/yyyy');
  };

  const loadCurrentMonthPackages = useCallback(async () => {
    if (!licenseId) return;

    setLoadingPackages(true);
    try {
      const response = await api.get(`/licenses/${licenseId}/extra-packages/current-month`);
      setCurrentMonthPackages(response.data || []);
    } finally {
      setLoadingPackages(false);
    }
  }, [api, licenseId]);

  useEffect(() => {
    loadCurrentMonthPackages();
  }, [loadCurrentMonthPackages]);

  const columns: ITableColumn[] = [
    {
      id: 'package_value',
      name: 'Valor do pacote',
      responsive: false,
      dataIndex: 'package_value',
      renderColumn(data: IExtraPackageCurrentMonth) {
        return <span>{formatPackageValue(data.package_value)}</span>;
      },
    },
    {
      id: 'hiring_date',
      name: 'Data de contratação',
      responsive: false,
      dataIndex: 'hiring_date',
      renderColumn(data: IExtraPackageCurrentMonth) {
        return <span>{formatHiringDate(data.hiring_date)}</span>;
      },
    },
    {
      id: 'credit',
      name: 'Tipo',
      responsive: false,
      dataIndex: 'credit',
      renderColumn(data: IExtraPackageCurrentMonth) {
        return <span>{data.credit ? 'Crédito' : 'Nova contratação'}</span>;
      },
    },
  ];

  const handleSubmit = async () => {
    const parsedValue = parseCurrency(extraPackageValue);
    if (parsedValue <= 0) return;
    
    setLoading(true);
    try {
      await onSave(parsedValue);
      setExtraPackageValue('');
      await loadCurrentMonthPackages();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 0', minHeight: '300px' }}>
      <Row gutter={4} align="middle">
        <Col span={2}>
          <span style={{ fontSize: 16 }}>Pacote extra</span>
        </Col>

        <Col span={8}>
          <Form layout="vertical">
            <Form.Item
              label="Valor para o novo pacote"
              style={{ margin: 0 }}
              required
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <Input
                style={{ width: '100%', borderRadius: 16 }}
                placeholder="R$ 0,00"
                value={extraPackageValue}
                onChange={e =>
                  setExtraPackageValue(e.target.value.replace(/[^\d,.]/g, ''))
                }
                onBlur={e =>
                  setExtraPackageValue(formatCurrency(e.target.value))
                }
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <div style={{ marginTop: '48px' }}>
        <Space size="middle">
          <Button
            type="primary"
            shape="round"
            icon={<SaveFilled />}
            onClick={handleSubmit}
            loading={loading}
            disabled={parseCurrency(extraPackageValue) <= 0}
          >
            Salvar
          </Button>
          <Button
            shape="round"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        </Space>
      </div>

      <div style={{ marginTop: '48px' }}>
        <Table
          title="Pacotes extras do mês atual"
          data={currentMonthPackages}
          tableColumns={columns}
          keyIndex="hiring_date"
          isPaginated={false}
          action={false}
          loading={loadingPackages}
          emptyMessage="Não há pacotes extras contratados para o mês vigente."
        />
      </div>
    </div>
  );
};

export default LicenseExtraPackageForm;